import { useContext, useEffect, useMemo, useState } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { TestingContext } from '../../context/TestingContext';
import TESTING_PLACE_SCHEDULE_STATUS from '../../constants/testing_place_schedule';
import {
  get_testing_list,
  get_testing_place_list,
  get_testing_schedule_list,
  get_testing_place_schedule_list,
  get_testing_accept_summary,
  upsert_testing_place,
  upsert_testing_place_schedule,
} from '../../utils/restapi';
import { getCurrentRegularTesting } from '../../utils/currentTesting';
import './plan.css';
import ListBody from '../../components/list/ListBody';

// 진행위원 역할 — testing_teacher/setup.js 와 동일한 5개 역할을 그대로 사용한다
// (기존 페이지가 testing_place.data.teachers 에 이미 쓰고 있는 필드명과 호환).
// auto:true 인 역할(정/부감독)은 고사실 구성에서 인원과 기본 업무가 자동 산출되며 직접 입력하지 않는다.
const TEACHER_ROLES = [
  { key: 'master', label: '총감독위원', placeholder: '예: 고사장 전체 총괄, 민원 및 비상 상황 대응' },
  { key: 'main', label: '시험감독위원(정)', auto: true, autoNote: '고사실 1실당 1명 자동 산출' },
  { key: 'sub', label: '시험감독위원(부)', auto: true, autoNote: '고사실 수용인원 30명당 1명 자동 산출' },
  { key: 'info', label: '통제위원', placeholder: '예: 정·후문 출입 통제, 수험생 동선 안내 및 소음 관리, 복도 감독' },
  { key: 'reserve', label: '관리위원', placeholder: '예: 타종 등 방송 시설 제어, 수험표 출력 및 행정 지원, 시험 준비 및 정리' },
];

// 부감독 1명이 담당하는 인원 기준 — 30명 이상 1명, 60명 이상 2명 …
const SUB_TEACHER_PER_USERS = 30;

// 자동 기재된 기본 업무에 사용자가 덧붙인 내용을 합쳐 최종 업무 문구를 만든다.
// 예: composeDuty('1고사실 시험감독(정)', '사전 책상 배치') → '1고사실 시험감독(정), 사전 책상 배치'
const composeDuty = (base, extra) => (extra && extra.trim() ? `${base}, ${extra.trim()}` : base);

const REPORT_STATUS_LABEL = {
  draft: '작성 중',
  submitted: '보고 완료',
  approved: '본부 승인',
};

// 4단계 프로세스 — 상단 네비게이션과 섹션 id 매핑에 공통으로 사용
// (기본 정보는 별도 고정 영역이라 프로세스 목록에서 제외)
const PROCESSES = [
  { no: 2, id: 'plan-section-2', label: '고사실 세부 설정' },
  { no: 3, id: 'plan-section-3', label: '진행 위원 배치 계획' },
  { no: 4, id: 'plan-section-4', label: '시설사용료 계획서' },
  { no: 5, id: 'plan-section-5', label: '특이사항 및 최종 보고' },
];

const emptyRoom = (testingPlaceObj, testingScheduleObj, code) => ({
  testing_place_id: testingPlaceObj.testing_place_id,
  testing_schedule_id: testingScheduleObj.testing_schedule_id,
  code: String(code),
  room_no: `${code}고사실`,
  max_users: 0,
  main_teachers: 1,
  sub_teachers: 0,
  data: { seat: { rows: 0, columns: 0, direction: 'vertical', raw: '' }, locationName: '' },
  status: TESTING_PLACE_SCHEDULE_STATUS.field.join,
});

// 고사실 목록에서 정/부감독의 기본 업무 문구를 산출한다.
//  - 정감독: 고사실마다 1명
//  - 부감독: 고사실 수용인원 30명당 1명 (30명↑ 1명, 60명↑ 2명 …)
const buildAutoTeacherDuties = (rooms) => {
  const main = [];
  const sub = [];
  rooms.forEach((room, index) => {
    const roomLabel = (room.room_no || '').trim() || `${room.code || index + 1}고사실`;
    main.push(`${roomLabel} 시험감독(정)`);
    const subCount = Math.floor((parseInt(room.max_users, 10) || 0) / SUB_TEACHER_PER_USERS);
    for (let i = 0; i < subCount; i += 1) sub.push(`${roomLabel} 시험감독(부)`);
  });
  return { main, sub };
};

const TestingPlaceSchedulePlan = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { testingObj, setTestingObj } = useContext(TestingContext);

  // 시험유형 전체 목록 · 선택 유형의 회차 목록 · 현재 선택 유형 코드.
  // 테스트용으로 구분(유형)·시험(회차)·일정을 모두 선택할 수 있게 보관한다.
  const [testingTypes, setTestingTypes] = useState([]);
  const [testings, setTestings] = useState([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [testingSchedules, setTestingSchedules] = useState([]);
  const [testingSchedule, setTestingSchedule] = useState(null);
  const [testingPlaces, setTestingPlaces] = useState([]);
  const [testingPlace, setTestingPlace] = useState(null);
  // 최종 확정 접수 인원 — testing_place.accept_users 요약컬럼이 갱신 안 돼 0 으로 남는 경우가 있어,
  // testing_accept 에서 지역본부(hcodes) 무관하게 라이브 집계한 값으로 덮어쓴다. (list.js 와 동일 전략)
  // { [testing_place_id]: 접수인원 } 형태로 지역본부 전체 고사장 분을 함께 담아 기본정보 현황표에도 쓴다.
  const [acceptUsersMap, setAcceptUsersMap] = useState({});

  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState({ master: 0, main: 0, sub: 0, info: 0, reserve: 0, duties: {}, extras: {} });
  const [costs, setCosts] = useState([]);
  const [notes, setNotes] = useState('');
  const [reportStatus, setReportStatus] = useState('draft');
  // 각 단계의 작성 완료 표시 — 진행 상황 배지 용도이며 다른 단계의 편집을 막지는 않는다.
  const [stepConfirmed, setStepConfirmed] = useState({ rooms: false, teachers: false, costs: false });
  // 현재 화면에 열려 있는 단계 (2~5) — 상단 탭으로 자유롭게 이동한다.
  const [activeStep, setActiveStep] = useState(2);
  // 5단계에서 "제출 전 내용 확인" 화면 표시 여부 (임시 UI 상태, 저장 대상 아님)
  const [reviewMode, setReviewMode] = useState(false);
  // 기본 정보의 "지역본부 고사장 전체 현황" 펼침 여부
  const [showHqOverview, setShowHqOverview] = useState(false);

  const isLocked = reportStatus !== 'draft';

  // ---- 조회 ----
  // 구분(정기시험)·시험 회차는 선택 대상이 아니라 '이번 회차'로 자동 고정한다.
  const initCurrentTesting = async () => {
    try {
      const { types, regularType, testing, regularTestings: allTestings } = await getCurrentRegularTesting();
      setTestingTypes(types || []);
      setTestings(allTestings || []);
      setSelectedTypeCode(regularType?.code || '');
      if (!regularType?.code || !testing) {
        setTestingObj(null);
        return;
      }
      setTestingObj({ ...testing, type: regularType.code });
    } catch (error) {
      console.error('이번 회차 조회 실패:', error);
    }
  };

  // 일정도 선택이 아니라 해당 회차의 일정으로 자동 셋팅한다.
  // (회차에 일정이 여러 건인 경우에만 전환용 셀렉트를 노출한다)
  const getTestingSchedules = async (testing_id) => {
    if (!testing_id) { setTestingSchedules([]); setTestingSchedule(null); return; }
    try {
      const response = await get_testing_schedule_list({ testing_id, page: 1, rowsPerPage: 1000 });
      const lists = response.lists || [];
      setTestingSchedules(lists);
      setTestingSchedule(lists[0] || null);
    } catch (error) {
      console.error('일정 조회 실패:', error);
      setTestingSchedules([]);
      setTestingSchedule(null);
    }
  };

  // 고사장 목록은 로그인 담당자가 관리하는 지역본부(testingHqs)의 gcode 로만 조회한다 —
  // 다른 지역본부 고사장은 선택 목록에도, 기본정보 현황표에도 나타나지 않는다.
  const getTestingPlaces = async () => {
    if (!testingObj?.testing_id || testingHqs.length === 0) { setTestingPlaces([]); return; }
    try {
      const response = await get_testing_place_list({
        testing_id: testingObj.testing_id,
        gcodes: testingHqs.map((hq) => hq.code).join(','),
        page: 1,
        rowsPerPage: 1000,
        sort: 'a_gcode,a_code',
      });
      setTestingPlaces(response.lists || []);
      if (response.lists?.length === 1) setTestingPlace(response.lists[0]);
    } catch (error) {
      console.error('고사장 조회 실패:', error);
      setTestingPlaces([]);
    }
  };

  // testing_place.rooms 는 응시자명단/좌석배치도 등 다른 화면이 참조하는 요약 컬럼이라
  // 실제 고사실 개수와 어긋나 있으면 여기서 바로잡는다.
  const syncTestingPlaceRooms = async (count) => {
    if (!testingPlace?.testing_place_id) return;
    if (parseInt(testingPlace.rooms, 10) === count) return;
    try {
      await upsert_testing_place({ testing_place_id: testingPlace.testing_place_id, rooms: count });
      setTestingPlace((prev) => (prev ? { ...prev, rooms: count } : prev));
    } catch (error) {
      console.error('고사실수 동기화 실패:', error);
    }
  };

  const getRooms = async () => {
    if (!testingPlace?.testing_place_id || !testingSchedule?.testing_schedule_id) { setRooms([]); return; }
    try {
      const response = await get_testing_place_schedule_list({
        testing_place_id: testingPlace.testing_place_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
        page: 1,
        rowsPerPage: 1000,
        sort: 'a_code*1',
        desc: 'asc',
      });
      const lists = (response.lists || []).filter((row) =>
        TESTING_PLACE_SCHEDULE_STATUS.used.includes(parseInt(row.status, 10))
      );
      setRooms(lists.length > 0 ? lists : [emptyRoom(testingPlace, testingSchedule, 1)]);
      syncTestingPlaceRooms(lists.length);
    } catch (error) {
      console.error('고사실 조회 실패:', error);
      setRooms([]);
    }
  };

  useEffect(() => {
    initCurrentTesting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testingObj?.testing_id) {
      getTestingSchedules(testingObj.testing_id);
      getTestingPlaces();
    } else {
      setTestingSchedules([]);
      setTestingPlaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.testing_id, testingHqs.length]);

  // 선택 고사장 뿐 아니라 지역본부 전체 고사장의 접수인원을 한 번에 받아 둔다.
  // (기본 정보의 고사장 전체 현황표에서 그대로 사용)
  const getAcceptSummary = async () => {
    if (!testingObj?.testing_id || !testingSchedule?.testing_schedule_id) {
      setAcceptUsersMap({});
      return;
    }
    try {
      const response = await get_testing_accept_summary({
        testing_id: testingObj.testing_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
      });
      const map = {};
      (response.lists || []).forEach((row) => {
        map[Number(row.testing_place_id)] = parseInt(row.accept_users, 10) || 0;
      });
      setAcceptUsersMap(map);
    } catch (error) {
      console.error('접수인원 집계 조회 실패:', error);
      setAcceptUsersMap({});
    }
  };

  useEffect(() => {
    if (testingSchedule?.testing_schedule_id) getAcceptSummary();
    else setAcceptUsersMap({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.testing_id, testingSchedule?.testing_schedule_id]);

  useEffect(() => {
    if (testingPlace?.testing_place_id && testingSchedule?.testing_schedule_id) getRooms();
    else setRooms([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingPlace?.testing_place_id, testingSchedule?.testing_schedule_id]);

  // testingPlace 가 바뀌면 Section 3~5 상태를 해당 고사장의 data 로 초기화
  useEffect(() => {
    const data = testingPlace?.data || {};
    const counts = {
      master: data.teachers?.master || 0,
      main: data.teachers?.main || 0,
      sub: data.teachers?.sub || 0,
      info: data.teachers?.info || 0,
      reserve: data.teachers?.reserve || 0,
    };
    const rawDuties = data.teachers?.duties || {};
    const duties = {};
    for (const key of Object.keys(counts)) {
      const count = parseInt(counts[key], 10) || 0;
      const prevList = Array.isArray(rawDuties[key]) ? rawDuties[key] : [];
      duties[key] = Array.from({ length: count }, (_, i) => prevList[i] || '');
    }
    // 정/부감독은 자동 산출이라 사용자가 덧붙인 내용(extras)만 복원한다.
    const rawExtras = data.teachers?.extras || {};
    const extras = {
      main: Array.isArray(rawExtras.main) ? rawExtras.main : [],
      sub: Array.isArray(rawExtras.sub) ? rawExtras.sub : [],
    };
    setTeachers({ ...counts, duties, extras });
    setCosts(data.costs || []);
    setNotes(data.notes || '');
    setReportStatus(data.reportStatus || 'draft');
    setStepConfirmed({
      rooms: !!data.stepConfirmed?.rooms,
      teachers: !!data.stepConfirmed?.teachers,
      costs: !!data.stepConfirmed?.costs,
    });
    setReviewMode(false);
    setActiveStep(2);
  }, [testingPlace]);

  // ---- 셀렉터 핸들러 ----
  // 선택 유형의 회차 목록을 조회한다(최신순).
  const getTestingsByType = async (typeCode) => {
    if (!typeCode) return [];
    try {
      const response = await get_testing_list({
        type: typeCode, page: 1, rowsPerPage: 1000, sort: 'a_testing_id', desc: 'desc',
      });
      return response?.lists || response || [];
    } catch (error) {
      console.error('회차 조회 실패:', error);
      return [];
    }
  };

  // 테스트용 구분(유형) 선택 — 유형을 바꾸면 그 유형의 회차 목록을 다시 받아 첫 회차로 셋팅한다.
  const handleTypeChange = async (e) => {
    const code = e.target.value;
    setSelectedTypeCode(code);
    setTestingPlace(null);
    const lists = await getTestingsByType(code);
    setTestings(lists);
    setTestingObj(lists[0] ? { ...lists[0], type: code } : null);
  };

  // 테스트용 회차 선택 — 기본값은 이번 회차이며, 지난 회차를 골라 그 데이터로 진행해 볼 수 있다.
  // 회차를 바꾸면 하위(일정·고사장·고사실)는 testingObj 변경 effect 가 다시 조회한다.
  const handleTestingChange = (e) => {
    const selected = testings.find((o) => String(o.testing_id) === e.target.value);
    if (!selected) return;
    setTestingPlace(null);
    setTestingObj({ ...selected, type: selectedTypeCode || testingObj?.type });
  };
  const handleTestingScheduleChange = (e) => {
    const selected = testingSchedules.find((o) => o.testing_schedule_id === parseInt(e.target.value));
    setTestingSchedule(selected || null);
  };
  const handleTestingPlaceChange = (e) => {
    const selected = testingPlaces.find((o) => o.testing_place_id === parseInt(e.target.value));
    setTestingPlace(selected || null);
  };

  // ---- 상단 프로세스 네비게이션 ----
  // 단계는 순서에 상관없이 자유롭게 오갈 수 있다 — 확정 여부는 진행 상황 표시일 뿐 이동을 막지 않는다.
  const PROCESS_CONFIRM_KEY = { 2: 'rooms', 3: 'teachers', 4: 'costs' };

  const handleGoToProcess = (p) => {
    setReviewMode(false);
    setActiveStep(p.no);
    const el = document.getElementById(p.id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Section 2: 고사실 ----
  const persistRoom = async (room) => {
    try {
      await upsert_testing_place_schedule({
        testing_place_schedule_id: room.testing_place_schedule_id,
        testing_place_id: testingPlace.testing_place_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
        code: room.code,
        room_no: room.room_no,
        max_users: room.max_users,
        main_teachers: room.main_teachers,
        sub_teachers: room.sub_teachers,
        data: room.data,
        status: room.status,
      });
    } catch (error) {
      console.error('고사실 저장 실패:', error);
      alert('고사실 정보를 저장하는데 실패했습니다.');
    }
  };

  const updateRoom = (index, patch) => {
    setRooms((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row;
        // patch 로 넘어온 status(삭제 등)가 있으면 그대로 쓰고, 없을 때만 join 으로 되돌린다.
        // (이전에는 무조건 join 으로 덮어써서 고사실 삭제가 서버에 반영되지 않았다)
        const merged = { ...row, ...patch, status: patch.status ?? TESTING_PLACE_SCHEDULE_STATUS.field.join };
        // 감독 인원은 진행위원 배치 계획과 같은 규칙으로 고사실 행에도 함께 반영한다.
        if (merged.status !== TESTING_PLACE_SCHEDULE_STATUS.field.delete) {
          merged.main_teachers = 1;
          merged.sub_teachers = Math.floor((parseInt(merged.max_users, 10) || 0) / SUB_TEACHER_PER_USERS);
        }
        return merged;
      });
      persistRoom(next[index]);
      return next;
    });
  };

  const handleAddRoom = () => {
    const nextCode = rooms.length + 1;
    setRooms((prev) => [...prev, emptyRoom(testingPlace, testingSchedule, nextCode)]);
  };

  const handleRemoveRoom = (index) => {
    const room = rooms[index];
    if (!window.confirm('고사실을 삭제하시겠습니까?')) return;
    if (room.testing_place_schedule_id) {
      updateRoom(index, { status: TESTING_PLACE_SCHEDULE_STATUS.field.delete });
      setRooms((prev) => prev.filter((_, i) => i !== index));
    } else {
      setRooms((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // 좌석 배치 형태 — "열*행"(예: 5*4) 로 입력받아 columns(열)/rows(행) 로 분해한다.
  // master 앱의 회차별 고사실 설정(testing_place_schedule/setup)과 동일하게 columns=열, rows=행.
  // 입력 도중의 문자열도 잃지 않도록 raw 원문을 함께 보관한다.
  const handleSeatSizeChange = (index, item, value) => {
    const matched = value.match(/^\s*(\d+)\s*[*xX×]\s*(\d+)\s*$/);
    const seat = { ...item.data?.seat, raw: value };
    if (matched) {
      seat.columns = parseInt(matched[1], 10);
      seat.rows = parseInt(matched[2], 10);
    }
    updateRoom(index, { data: { ...item.data, seat } });
  };

  const seatSizeText = (item) => {
    if (item.data?.seat?.raw != null) return item.data.seat.raw;
    const { columns, rows } = item.data?.seat || {};
    return columns && rows ? `${columns}*${rows}` : '';
  };

  const seatCountOf = (item) => {
    const { columns, rows } = item.data?.seat || {};
    return (parseInt(columns, 10) || 0) * (parseInt(rows, 10) || 0);
  };

  const roomsMaxUsersSum = rooms.reduce((sum, r) => sum + (parseInt(r.max_users, 10) || 0), 0);
  const acceptUsers = acceptUsersMap[Number(testingPlace?.testing_place_id)]
    ?? (parseInt(testingPlace?.accept_users, 10) || 0);
  const roomsMismatch = acceptUsers > 0 && roomsMaxUsersSum !== acceptUsers;

  // ---- Section 3: 진행 위원 배치 계획 ----
  // 정/부감독은 고사실 구성에서 자동 산출된다.
  const autoDuties = useMemo(() => buildAutoTeacherDuties(rooms), [rooms]);

  const handleTeacherCountChange = (key, value) => {
    const count = Math.max(0, parseInt(value, 10) || 0);
    setTeachers((prev) => {
      const prevDuties = Array.isArray(prev.duties?.[key]) ? prev.duties[key] : [];
      const nextDuties = Array.from({ length: count }, (_, i) => prevDuties[i] || '');
      return { ...prev, [key]: value, duties: { ...prev.duties, [key]: nextDuties } };
    });
  };
  const handleTeacherDutyItemChange = (key, index, value) => {
    setTeachers((prev) => {
      const list = Array.isArray(prev.duties?.[key]) ? [...prev.duties[key]] : [];
      list[index] = value;
      return { ...prev, duties: { ...prev.duties, [key]: list } };
    });
  };
  // 자동 역할(정/부감독)의 "추가로 적는 업무" 입력
  const handleTeacherExtraChange = (key, index, value) => {
    setTeachers((prev) => {
      const list = Array.isArray(prev.extras?.[key]) ? [...prev.extras[key]] : [];
      list[index] = value;
      return { ...prev, extras: { ...prev.extras, [key]: list } };
    });
  };

  // 저장·검토·총계에 쓰는 최종 진행위원 값 — 자동 역할은 산출값으로 덮어쓴다.
  const resolvedTeachers = useMemo(() => ({
    ...teachers,
    main: autoDuties.main.length,
    sub: autoDuties.sub.length,
    duties: {
      ...teachers.duties,
      main: autoDuties.main.map((base, i) => composeDuty(base, teachers.extras?.main?.[i])),
      sub: autoDuties.sub.map((base, i) => composeDuty(base, teachers.extras?.sub?.[i])),
    },
  }), [teachers, autoDuties]);

  const teacherCountOf = (role) => (role.auto
    ? autoDuties[role.key].length
    : parseInt(teachers[role.key], 10) || 0);
  const teachersTotal = TEACHER_ROLES.reduce((sum, role) => sum + teacherCountOf(role), 0);

  // ---- Section 4: 시설사용료 ----
  const handleAddCost = () => setCosts((prev) => [...prev, { item: '', calc: '', amount: 0, note: '' }]);
  const handleRemoveCost = (index) => setCosts((prev) => prev.filter((_, i) => i !== index));
  const handleCostChange = (index, field, value) => {
    setCosts((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };
  const costsTotal = costs.reduce((sum, c) => sum + (parseInt(c.amount, 10) || 0), 0);

  // ---- 편집 가능 여부 ----
  // 단계 간 이동이 자유로워졌으므로 순차 잠금은 두지 않는다.
  // 본부 보고를 마친 뒤(isLocked)에만 전체가 읽기 전용이 된다.
  const roomsDisabled = isLocked;
  const teachersDisabled = isLocked;
  const costsDisabled = isLocked;
  const notesDisabled = isLocked || reviewMode;

  // 상단 탭에서 고른 단계만 보여준다.
  // 이미 보고까지 끝난 경우(isLocked)에는 제출된 전체 내용을 한 번에 볼 수 있게 모두 표시한다.
  const currentSectionNo = isLocked ? null : activeStep;
  const showSection = (no) => currentSectionNo === null || currentSectionNo === no;

  const roomHeaders = [
    { name: 'NO.', value: '', renderCell: (item) => item.code },
    {
      name: '고사실 번호', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.room_no} disabled={roomsDisabled}
          onChange={(e) => updateRoom(index, { room_no: e.target.value })} />
      ),
      renderFooterCell: () => (
        <button onClick={handleAddRoom} disabled={roomsDisabled}>고사실 추가</button>
      ),
    },
    {
      name: '실제 장소명(선택)', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.data?.locationName || ''} disabled={roomsDisabled}
          placeholder="예: 1학년 1반"
          onChange={(e) => updateRoom(index, { data: { ...item.data, locationName: e.target.value } })} />
      ),
    },
    {
      name: '수용 인원', value: '',
      renderCell: (item, index) => (
        <input type="number" min="0" value={item.max_users} disabled={roomsDisabled}
          onChange={(e) => updateRoom(index, { max_users: e.target.value })} />
      ),
      renderFooterCell: () => (
        <span className={`sum-check ${roomsMismatch ? 'warn' : ''}`}>
          합계 {roomsMaxUsersSum}명
          {roomsMismatch && <b> (접수 {acceptUsers}명과 다릅니다)</b>}
        </span>
      ),
    },
    {
      name: '좌석 배치 형태', value: '',
      renderCell: (item, index) => {
        const seatCount = seatCountOf(item);
        const maxUsers = parseInt(item.max_users, 10) || 0;
        return (
          <span className="seat-cell">
            <input type="text" className="seat-size" disabled={roomsDisabled}
              placeholder="열*행 (예: 5*4)"
              value={seatSizeText(item)}
              onChange={(e) => handleSeatSizeChange(index, item, e.target.value)} />
            <select value={item.data?.seat?.direction || 'vertical'} disabled={roomsDisabled}
              onChange={(e) => updateRoom(index, { data: { ...item.data, seat: { ...item.data?.seat, direction: e.target.value } } })}>
              <option value="vertical">세로 배치</option>
              <option value="horizontal">가로 배치</option>
            </select>
            {seatCount > 0 && (
              <span className={`seat-hint ${maxUsers > 0 && seatCount < maxUsers ? 'warn' : ''}`}>
                좌석 {seatCount}석
                {maxUsers > 0 && seatCount < maxUsers && <b> (수용 {maxUsers}명보다 적습니다)</b>}
              </span>
            )}
          </span>
        );
      },
    },
    {
      name: '비고', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.data?.note || ''} disabled={roomsDisabled}
          placeholder="예: 휠체어석 필요"
          onChange={(e) => updateRoom(index, { data: { ...item.data, note: e.target.value } })} />
      ),
    },
    {
      name: '', value: '',
      renderCell: (item, index) => (
        <button onClick={() => handleRemoveRoom(index)} disabled={roomsDisabled}>삭제</button>
      ),
    },
  ];

  // ---- Section 5: 저장/확정/제출 ----
  const buildPlanData = (extra = {}) => ({
    ...testingPlace.data,
    teachers: resolvedTeachers,
    costs,
    notes,
    reportStatus,
    stepConfirmed,
    ...extra,
  });

  const handleSave = async (extra = {}) => {
    if (!testingPlace?.testing_place_id) {
      alert('고사장을 먼저 선택해 주세요.');
      return false;
    }
    const data = buildPlanData(extra);
    try {
      await upsert_testing_place({ testing_place_id: testingPlace.testing_place_id, data });
      setTestingPlace((prev) => ({ ...prev, data }));
      return true;
    } catch (error) {
      console.error('운영계획 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다.');
      return false;
    }
  };

  // 단계 "작성 완료" 표시 — 저장 후 진행 배지를 채우고 다음 단계로 넘어간다.
  // 다른 단계의 편집을 막지 않으므로 언제든 되돌아와 수정할 수 있다.
  const completeStep = async (key, nextStep) => {
    const nextStepConfirmed = { ...stepConfirmed, [key]: true };
    const ok = await handleSave({ stepConfirmed: nextStepConfirmed });
    if (ok) {
      setStepConfirmed(nextStepConfirmed);
      if (nextStep) setActiveStep(nextStep);
    }
  };

  const handleConfirmRooms = () => {
    if (roomsMismatch) {
      const msg = `고사실 수용인원 합계(${roomsMaxUsersSum}명)가 최종 확정 접수인원(${acceptUsers}명)과 다릅니다.\n그래도 저장하고 다음 단계로 이동할까요?`;
      if (!window.confirm(msg)) return;
    }
    completeStep('rooms', 3);
  };

  const handleConfirmTeachers = () => completeStep('teachers', 4);
  const handleConfirmCosts = () => completeStep('costs', 5);

  const handleSubmitReport = async () => {
    if (roomsMismatch) {
      const ok = window.confirm(
        `고사실 수용인원 합계(${roomsMaxUsersSum}명)가 최종 확정 접수인원(${acceptUsers}명)과 다릅니다.\n그래도 보고하시겠습니까?`
      );
      if (!ok) return;
    }
    if (!window.confirm('운영 계획을 본부에 보고하시겠습니까? 보고 후에는 수정할 수 없습니다.')) return;

    const extra = { reportStatus: 'submitted', submittedAt: new Date().toISOString() };
    const ok = await handleSave(extra);
    if (ok) {
      setReportStatus('submitted');
      setReviewMode(false);
      alert('운영 계획이 본부에 보고되었습니다.');
    }
  };

  const hqNameOf = (place) => testingHqs.find((hq) => hq.code === place?.gcode)?.name || '';
  const hqName = hqNameOf(testingPlace);

  // 수험표 출력시작일(confirmStartDate)부터 운영계획 보고를 진행할 수 있다.
  const now = new Date();
  const confirmStartDate = testingSchedule?.confirmStartDate ? new Date(testingSchedule.confirmStartDate) : null;
  const isBeforeConfirmStart = !(confirmStartDate && confirmStartDate <= now);

  // 담당 지역본부가 관리하는 고사장 전체 현황 — 어느 고사장이 아직 보고 전인지 한눈에 본다.
  const hqOverviewRows = testingPlaces.map((place) => ({
    place,
    hqName: hqNameOf(place),
    acceptUsers: acceptUsersMap[Number(place.testing_place_id)] ?? (parseInt(place.accept_users, 10) || 0),
    rooms: parseInt(place.rooms, 10) || 0,
    reportStatus: place.data?.reportStatus || 'draft',
  }));
  const hqOverviewTotal = hqOverviewRows.reduce((sum, row) => sum + row.acceptUsers, 0);

  const renderHqOverview = () => (
    <div className="hq-overview">
      <div className="hq-overview-head">
        <h3>지역본부 고사장 전체 현황 ({hqOverviewRows.length}개소 / 접수 {hqOverviewTotal}명)</h3>
        <button type="button" onClick={() => setShowHqOverview((prev) => !prev)}>
          {showHqOverview ? '접기' : '펼치기'}
        </button>
      </div>
      {showHqOverview && (
        <div className="table-scroll">
          <table className="hq-overview-table">
            <thead>
              <tr><th>지역본부</th><th>고사장명</th><th>접수 인원</th><th>고사실 수</th><th>보고 상태</th><th /></tr>
            </thead>
            <tbody>
              {hqOverviewRows.length === 0 ? (
                <tr><td colSpan={6}>조회된 고사장이 없습니다.</td></tr>
              ) : hqOverviewRows.map((row) => {
                const isCurrent = Number(row.place.testing_place_id) === Number(testingPlace?.testing_place_id);
                return (
                  <tr key={row.place.testing_place_id} className={isCurrent ? 'current' : ''}>
                    <td>{row.hqName}</td>
                    <td>{row.place.name}</td>
                    <td>{row.acceptUsers}명</td>
                    <td>{row.rooms}실</td>
                    <td><span className={`status-badge ${row.reportStatus}`}>{REPORT_STATUS_LABEL[row.reportStatus]}</span></td>
                    <td>
                      <button type="button" onClick={() => setTestingPlace(row.place)} disabled={isCurrent}>
                        {isCurrent ? '작성 중' : '작성하기'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div id="testing_place_schedule_plan">
      <div className="head">
        <h1>고사장 운영 계획</h1>
      </div>
      <div className="body">
        {/* 테스트용으로 구분(유형)·시험(회차)·일정을 모두 선택할 수 있다. 기본값은 이번 회차. */}
        <ul className="form">
          <li>
            <label>구분</label>
            <span>
              {testingTypes.length > 0 ? (
                <select value={selectedTypeCode || ''} onChange={handleTypeChange}>
                  {testingTypes.map((o, i) => (
                    <option key={i} value={o.code}>{o.nickname || o.name}</option>
                  ))}
                </select>
              ) : (
                <span className="fixed-value">불러오는 중입니다…</span>
              )}
            </span>
          </li>
          <li>
            <label>시험</label>
            <span>
              {testings.length > 0 ? (
                <select value={testingObj?.testing_id || ''} onChange={handleTestingChange}>
                  {testings.map((o, i) => (
                    <option key={i} value={o.testing_id}>{o.title}</option>
                  ))}
                </select>
              ) : (
                <span className="fixed-value">{testingObj?.title || '이번 회차를 불러오는 중입니다…'}</span>
              )}
            </span>
          </li>
          <li>
            <label>일정</label>
            <span>
              {testingSchedules.length > 0 ? (
                <select value={testingSchedule?.testing_schedule_id || ''} onChange={handleTestingScheduleChange}>
                  {testingSchedules.map((o, i) => <option key={i} value={o.testing_schedule_id}>{o.testStartDate} ~ {o.testStopDate}</option>)}
                </select>
              ) : (
                <span className="fixed-value">
                  {testingSchedule ? `${testingSchedule.testStartDate} ~ ${testingSchedule.testStopDate}` : '-'}
                </span>
              )}
            </span>
          </li>
          <li>
            <label>고사장</label>
            <span>
              {/* 로그인 담당자의 지역본부 고사장만 목록에 담긴다. */}
              <select value={testingPlace?.testing_place_id || ''} onChange={handleTestingPlaceChange}>
                <option value="">▒ 선택 ▒</option>
                {testingPlaces.map((o, i) => <option key={i} value={o.testing_place_id}>{o.name}</option>)}
              </select>
            </span>
          </li>
        </ul>

        {!testingPlace || !testingSchedule ? (
          <>
            <div className="alert">담당 지역본부의 고사장을 선택해 주세요.</div>
            {renderHqOverview()}
          </>
        ) : isBeforeConfirmStart ? (
          <div className="alert">
            수험표 출력시작일{confirmStartDate ? `(${confirmStartDate.toLocaleDateString('ko-KR')})` : ''}부터 운영 계획을 작성할 수 있습니다.
          </div>
        ) : (
          <>
            <section className="plan-section plan-section-sticky" id="plan-section-1">
              <h2>기본 정보</h2>
              <table className="info-table">
                <tbody>
                  <tr>
                    <th>시험 회차</th>
                    <td>{testingObj?.title}</td>
                    <th>지역본부 / 고사장명</th>
                    <td>{hqName} / {testingPlace.name}</td>
                    <th>최종 확정 접수 인원</th>
                    <td>{acceptUsers}명</td>
                  </tr>
                  <tr>
                    <th>진행 상태</th>
                    <td colSpan={5}>
                      <ol className="status-flow">
                        {['draft', 'submitted', 'approved'].map((step, i) => {
                          const stepIndex = ['draft', 'submitted', 'approved'].indexOf(reportStatus);
                          const state = i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo';
                          return (
                            <li key={step} className={state}>
                              <span className="status-label">{REPORT_STATUS_LABEL[step]}</span>
                            </li>
                          );
                        })}
                      </ol>
                    </td>
                  </tr>
                </tbody>
              </table>
              {renderHqOverview()}
            </section>

            <ol className="process-flow">
              {PROCESSES.map((p) => {
                const confirmKey = PROCESS_CONFIRM_KEY[p.no];
                const done = confirmKey ? !!stepConfirmed[confirmKey] : isLocked;
                const isCurrent = currentSectionNo === p.no;
                return (
                  <li
                    key={p.no}
                    className={isCurrent ? 'current' : done ? 'done' : 'todo'}
                    onClick={() => handleGoToProcess(p)}
                    title={`${p.label}로 이동`}
                  >
                    <span className="process-label">{p.label}</span>
                  </li>
                );
              })}
            </ol>

            {showSection(2) && (
            <section className="plan-section" id="plan-section-2">
              <div className="table-scroll">
                <ListBody headers={roomHeaders} items={rooms} className="room_table" showFooter />
              </div>
              {!isLocked && (
                <div className="step-actions">
                  <button className="confirm-step" onClick={handleConfirmRooms}>저장하고 다음 단계</button>
                </div>
              )}
            </section>
            )}

            {showSection(3) && (
            <section className="plan-section" id="plan-section-3">
              <p className="section-guide">
                시험감독위원(정)은 고사실 1실당 1명, 시험감독위원(부)는 고사실 수용인원 {SUB_TEACHER_PER_USERS}명당 1명으로
                자동 산출됩니다. 인원을 바꾸려면 고사실 세부 설정에서 고사실 수 또는 수용 인원을 조정해 주세요.
              </p>
              <div className="table-scroll">
                <table className="teacher-plan">
                  <thead>
                    <tr><th>구분</th><th>필요 인원</th><th>주요 업무 내용</th></tr>
                  </thead>
                  <tbody>
                    {TEACHER_ROLES.map((role) => (
                      <tr key={role.key}>
                        <td>{role.label}</td>
                        <td>
                          {role.auto ? (
                            <span className="count-cell auto">
                              <b>{teacherCountOf(role)}</b>
                              <span className="unit">명</span>
                              <span className="auto-note">{role.autoNote}</span>
                            </span>
                          ) : (
                            <span className="count-cell">
                              <input type="number" min="0" disabled={teachersDisabled}
                                value={teachers[role.key] ?? 0}
                                onChange={(e) => handleTeacherCountChange(role.key, e.target.value)} />
                              <span className="unit">명</span>
                            </span>
                          )}
                        </td>
                        <td>
                          {role.auto ? (
                            teacherCountOf(role) === 0 ? (
                              <span className="duty-empty">
                                고사실 세부 설정에서 {role.key === 'sub' ? `수용 인원이 ${SUB_TEACHER_PER_USERS}명 이상인 고사실을 등록하면` : '고사실을 등록하면'} 자동으로 배치됩니다.
                              </span>
                            ) : (
                              <div className="duty-list">
                                {autoDuties[role.key].map((base, i) => (
                                  <div className="duty-item" key={i}>
                                    <span className="duty-no">{i + 1}:</span>
                                    <span className="duty-base">{base}</span>
                                    <input type="text" disabled={teachersDisabled}
                                      placeholder="추가 업무 (예: 사전 책상 배치)"
                                      value={teachers.extras?.[role.key]?.[i] || ''}
                                      onChange={(e) => handleTeacherExtraChange(role.key, i, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (parseInt(teachers[role.key], 10) || 0) === 0 ? (
                            <span className="duty-empty">필요 인원을 입력하면 인원수만큼 업무 입력칸이 생깁니다.</span>
                          ) : (
                            <div className="duty-list">
                              {Array.from({ length: parseInt(teachers[role.key], 10) || 0 }, (_, i) => (
                                <div className="duty-item" key={i}>
                                  <span className="duty-no">{i + 1}:</span>
                                  <input type="text" disabled={teachersDisabled} placeholder={role.placeholder}
                                    value={teachers.duties?.[role.key]?.[i] || ''}
                                    onChange={(e) => handleTeacherDutyItemChange(role.key, i, e.target.value)} />
                                  <button type="button" className="duty-fill" disabled={teachersDisabled}
                                    onClick={() => handleTeacherDutyItemChange(role.key, i, role.placeholder)}>
                                    예시 입력
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="total"><td>총계</td><td>{teachersTotal}명</td><td /></tr>
                  </tbody>
                </table>
              </div>
              {!isLocked && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => setActiveStep(2)}>이전 단계</button>
                  <button className="confirm-step" onClick={handleConfirmTeachers}>저장하고 다음 단계</button>
                </div>
              )}
            </section>
            )}

            {showSection(4) && (
            <section className="plan-section" id="plan-section-4">
              <div className="table-scroll">
                <table className="cost-plan">
                  <thead>
                    <tr><th>항목명</th><th>산출 내역(단가, 수량, 시간 등)</th><th>예정 금액(원)</th><th>비고</th><th /></tr>
                  </thead>
                  <tbody>
                    {costs.map((row, index) => (
                      <tr key={index}>
                        <td><input type="text" disabled={costsDisabled} value={row.item} placeholder="예: 시설 대관료"
                          onChange={(e) => handleCostChange(index, 'item', e.target.value)} /></td>
                        <td><input type="text" disabled={costsDisabled} value={row.calc} placeholder="예: 55,000원 x 10실"
                          onChange={(e) => handleCostChange(index, 'calc', e.target.value)} /></td>
                        <td><input type="number" min="0" disabled={costsDisabled} value={row.amount}
                          onChange={(e) => handleCostChange(index, 'amount', e.target.value)} /></td>
                        <td><input type="text" disabled={costsDisabled} value={row.note}
                          onChange={(e) => handleCostChange(index, 'note', e.target.value)} /></td>
                        <td><button onClick={() => handleRemoveCost(index)} disabled={costsDisabled}>삭제</button></td>
                      </tr>
                    ))}
                    <tr className="total"><td colSpan={2}>합계</td><td>{costsTotal.toLocaleString()}원</td><td colSpan={2} /></tr>
                  </tbody>
                </table>
              </div>
              <div className="section-foot">
                <button onClick={handleAddCost} disabled={costsDisabled}>항목 추가</button>
              </div>
              {!isLocked && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => setActiveStep(3)}>이전 단계</button>
                  <button className="confirm-step" onClick={handleConfirmCosts}>저장하고 다음 단계</button>
                </div>
              )}
            </section>
            )}

            {showSection(5) && (
            <section className="plan-section" id="plan-section-5">
              <ul className="form-vertical">
                <li>
                  <label>협회(시행관리팀) 전달 사항</label>
                  <textarea disabled={notesDisabled} value={notes}
                    placeholder="예: 정문 공사로 인해 후문 개방 안내 요망, 주차장 통제 등 특이사항 기재"
                    onChange={(e) => setNotes(e.target.value)} />
                </li>
                <li>
                  <label>진행 상태</label>
                  <span className={`status-badge ${reportStatus}`}>{REPORT_STATUS_LABEL[reportStatus]}</span>
                </li>
              </ul>

              {!isLocked && !reviewMode && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => setActiveStep(4)}>이전 단계</button>
                  <button onClick={() => handleSave()}>임시 저장</button>
                  <button className="submit" onClick={() => setReviewMode(true)}>제출 전 내용 확인하기</button>
                </div>
              )}

              {!isLocked && reviewMode && (
                <div className="review-summary">
                  <h3>제출 전 최종 확인</h3>

                  <div className="review-block">
                    <h4>2. 고사실 세부 설정</h4>
                    <table className="review-table">
                      <thead>
                        <tr><th>NO.</th><th>고사실 번호</th><th>실제 장소명</th><th>수용 인원</th><th>좌석 배치</th><th>비고</th></tr>
                      </thead>
                      <tbody>
                        {rooms.map((r) => (
                          <tr key={r.code}>
                            <td>{r.code}</td>
                            <td>{r.room_no}</td>
                            <td>{r.data?.locationName || '-'}</td>
                            <td>{r.max_users}명</td>
                            <td>
                              {seatCountOf(r) > 0 ? `${r.data.seat.columns}*${r.data.seat.rows} ` : ''}
                              {r.data?.seat?.direction === 'horizontal' ? '가로 배치' : '세로 배치'}
                            </td>
                            <td>{r.data?.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="review-sum">수용인원 합계: {roomsMaxUsersSum}명</p>
                  </div>

                  <div className="review-block">
                    <h4>3. 진행 위원 배치 계획</h4>
                    <table className="review-table">
                      <thead><tr><th>구분</th><th>인원</th><th>업무 내용</th></tr></thead>
                      <tbody>
                        {TEACHER_ROLES.map((role) => (
                          <tr key={role.key}>
                            <td>{role.label}</td>
                            <td>{teacherCountOf(role)}명</td>
                            <td className="review-duties">
                              {(resolvedTeachers.duties?.[role.key] || []).some((d) => d)
                                ? resolvedTeachers.duties[role.key].map((d, i) => d ? <div key={i}>{i + 1}. {d}</div> : null)
                                : '-'}
                            </td>
                          </tr>
                        ))}
                        <tr className="total"><td>총계</td><td>{teachersTotal}명</td><td /></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="review-block">
                    <h4>4. 시설 사용료 내역</h4>
                    {costs.length === 0 ? (
                      <p>입력된 항목이 없습니다.</p>
                    ) : (
                      <table className="review-table">
                        <thead><tr><th>항목명</th><th>산출 내역</th><th>예정 금액</th><th>비고</th></tr></thead>
                        <tbody>
                          {costs.map((c, i) => (
                            <tr key={i}>
                              <td>{c.item || '-'}</td>
                              <td>{c.calc || '-'}</td>
                              <td>{(parseInt(c.amount, 10) || 0).toLocaleString()}원</td>
                              <td>{c.note || '-'}</td>
                            </tr>
                          ))}
                          <tr className="total"><td colSpan={2}>합계</td><td>{costsTotal.toLocaleString()}원</td><td /></tr>
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="review-block">
                    <h4>5. 특이사항</h4>
                    <p>{notes || '없음'}</p>
                  </div>

                  <div className="step-actions">
                    <button className="cancel-step" onClick={() => setReviewMode(false)}>수정하기</button>
                    <button className="submit" onClick={handleSubmitReport}>운영 계획 본부 보고하기</button>
                  </div>
                </div>
              )}
            </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TestingPlaceSchedulePlan;
